import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  List,
  ListItem,
  Typography,
  useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";

const faqList = [
  {
    title: "1. What is the Data Provenance Initiative?",
    detail:
      "The Data Provenance Initiative builds public measurement infrastructure for AI data, model ecosystems, and real-world use. Our work began with large-scale audits of training datasets and now also studies web consent, open model markets, concentration of power, and how people use and misuse AI systems.",
  },
  {
    title: "2. What dashboards and tools does DPI maintain?",
    detail:
      "DPI maintains several public resources for inspecting different parts of the AI ecosystem:",
    detailList: [
      "The Data Provenance Explorer audits sources, licenses, creators, languages, geographies, and modalities across 4,000+ text, speech, and video datasets.",
      "The Web Consent Explorer tracks terms, robots.txt, and crawl restrictions across 14,000 web domains used in AI training corpora.",
      "The Open Model Evolution dashboard monitors 851k open models, 2.2B downloads, and 200+ model attributes on Hugging Face.",
      "The AI Observatory compares 23,158 real-use AI conversations and 85,633 turns across 145 annotated features from 7 public sources.",
    ],
  },
  {
    title: "3. Why does provenance and ecosystem measurement matter?",
    detail:
      "AI systems are shaped by their training data, model supply chains, platform dynamics, and real-world usage contexts. Measuring these systems helps researchers, developers, policymakers, journalists, and the public understand where power is concentrated, what data is available or restricted, who is represented, and where transparency is breaking down.",
  },
  {
    title: "4. Who can benefit from DPI's resources?",
    detail: "The initiative's tools and findings are useful for:",
    detailList: [
      "Researchers studying training data, model ecosystems, web governance, and AI usage patterns.",
      "AI developers seeking better-documented datasets and clearer signals about licensing, attribution, and consent.",
      "Policymakers and legal professionals evaluating data governance, copyright, concentration, and transparency questions.",
      "Journalists, civil society groups, and public-interest technologists looking for empirical evidence about the AI ecosystem.",
    ],
  },
  {
    title: "5. Are DPI datasets and dashboards public?",
    detail:
      "We aim to release public papers, datasets, dashboards, and documentation whenever possible. Some resources link to external platforms such as GitHub, Hugging Face, and the AI Observatory, while others are hosted directly on this website.",
  },
  {
    title: "6. How can I contribute to the Data Provenance Initiative?",
    detail: "Individuals can contribute by:",
    detailList: [
      "Participating in dataset audits, annotations, and validation work.",
      "Providing feedback on dashboards, data quality, and documentation.",
      "Collaborating on research related to AI data, model ecosystems, real-world usage, transparency, law, and governance.",
      <>
        For more information, email{" "}
        <Link to="mailto:data.provenance.init@gmail.com">
          <Typography
            component="span"
            sx={{
              fontSize: { lg: "24px", md: "20px", xs: "18px" },
              fontWeight: "500",
              color: "#AC482A",
              wordBreak: "break-word",
              textDecoration: "underline",
            }}
          >
            data.provenance.init@gmail.com
          </Typography>
        </Link>
        .
      </>,
    ],
  },
];

const FAQ = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: "100%",
        overflow: "auto",
        padding: { lg: "80px 0 125px", sm: "80px 0 100px", xs: "50px 0 80px" },
      }}
    >
      <Grid container spacing={3}>
        <Grid item md={12} xs={12}>
          <Box sx={{ marginBottom: { lg: "50px", md: "30px", xs: "20px" } }}>
            <Typography
              sx={{
                fontSize: { lg: "96px", md: "80px", sm: "60px", xs: "48px" },
                lineHeight: "100%",
                fontWeight: "500",
                color: theme.customColors.primary.prusianBlue,
                width: { lg: "70%", xs: "100%" },
              }}
              variant="h1"
            >
              Frequently Asked Questions
            </Typography>
          </Box>
        </Grid>
        <Grid item md={12} xs={12}>
          <Box>
            {faqList.map((item, index) => (
              <Accordion
                key={index}
                sx={{
                  backgroundColor: "inherit",
                  boxShadow: "none",
                  borderBottom: "1px solid",
                  borderBottomColor: theme.customColors.primary.prusianBlue,
                  margin: "0 !important",
                  borderRadius: "0 !important",
                  ".MuiAccordion-root": {
                    ":before": {
                      height: 0,
                    },
                    ":nth-last-child(1)": {
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    },
                  },
                  ".Mui-expanded": {
                    margin: "0px !important",
                    borderBottomColor: theme.customColors.primary.lion,
                  },
                }}
              >
                <AccordionSummary
                  id="panel-header"
                  aria-controls="panel-content"
                  sx={{
                    border: "0",
                    padding: "15px 12px",
                    margin: "0",
                    ".MuiAccordionSummary-content": {
                      margin: 0,
                    },
                    ".Mui-expanded": {
                      "*": {
                        fontWeight: 600,
                        color: theme.customColors.primary.lion,
                        margin: "0px !important",
                      },
                    },
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: { lg: "24px", md: "20px", xs: "18px" },
                      fontWeight: "500",
                      color: theme.customColors.primary.prusianBlue,
                    }}
                  >
                    {item.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: "15px 30px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { lg: "24px", md: "20px", xs: "18px" },
                      fontWeight: "500",
                      color: theme.customColors.primary.prusianBlue,
                    }}
                  >
                    {item.detail}
                  </Typography>
                  <List
                    sx={{
                      padding: "10px 0 0 18px",
                      listStyleType: "disc",
                    }}
                  >
                    {item?.detailList?.map((childItem, index) => {
                      return (
                        <ListItem
                          key={index}
                          sx={{
                            display: "list-item",
                            padding: "0",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: { lg: "24px", md: "20px", xs: "18px" },
                              fontWeight: "500",
                              color: theme.customColors.primary.prusianBlue,
                            }}
                          >
                            {childItem}
                          </Typography>
                        </ListItem>
                      );
                    })}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FAQ;
